type SelectionEventSource = {
    addEventListener(type: "select", listener: EventListener): void
    removeEventListener(type: "select", listener: EventListener): void
}

export const NATIVE_SELECTION_REGION_CLASS = "__native-selection-region"

/**
 * Keeps the browser's native text selection and the reader's custom selection
 * mutually exclusive.
 */
export class NativeSelectionBridge {
    readonly #document: Document
    readonly #customSelectionEvents: SelectionEventSource
    readonly #cancelCustomSelection: () => void

    readonly #onNativeSelectionChange = () => {
        const selection = this.#document.getSelection()
        if (!selection || selection.rangeCount === 0 || selection.isCollapsed) return
        if (selection.toString().length === 0) return

        this.#cancelCustomSelection()
    }

    readonly #onCustomSelection = () => {
        const selection = this.#document.getSelection()
        if (!selection || selection.rangeCount === 0) return

        selection.removeAllRanges()
    }

    /**
     * Android WebView keeps its native selection active when a tap is handled
     * by the app's custom reader listeners. Observe the tap without consuming
     * it, and only clear the native range when it began outside the selectable
     * region.
     */
    readonly #onPointerDown = (event: PointerEvent) => {
        const target = event.target
        if (target instanceof Element && target.closest(`.${NATIVE_SELECTION_REGION_CLASS}`)) {
            return
        }

        const selection = this.#document.getSelection()
        if (!selection || selection.rangeCount === 0 || selection.isCollapsed) return

        selection.removeAllRanges()
    }

    constructor(
        document: Document,
        customSelectionEvents: SelectionEventSource,
        cancelCustomSelection: () => void,
    ) {
        this.#document = document
        this.#customSelectionEvents = customSelectionEvents
        this.#cancelCustomSelection = cancelCustomSelection
        this.#document.addEventListener("selectionchange", this.#onNativeSelectionChange)
        this.#document.addEventListener("pointerdown", this.#onPointerDown, {
            capture: true,
            passive: true,
        })
        this.#customSelectionEvents.addEventListener("select", this.#onCustomSelection)
    }

    destroy() {
        this.#document.removeEventListener("selectionchange", this.#onNativeSelectionChange)
        this.#document.removeEventListener("pointerdown", this.#onPointerDown, true)
        this.#customSelectionEvents.removeEventListener("select", this.#onCustomSelection)
    }
}
